import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { fetchSite, getToken, updateNewsArticle, uploadFile } from './api';
import ArticleForm from './ArticleForm';
import { AdminImagePreview } from './adminUi';

export default function NewsEdit() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    let cancelled = false;
    setLoadState('loading');

    function applyData(data) {
      if (cancelled) return;
      const decodedId = articleId ? decodeURIComponent(articleId) : '';
      const row = data.news?.articles?.find((a) => a.id === decodedId);
      if (!row) {
        setArticle(null);
        setLoadState('notfound');
        return;
      }
      setArticle({ ...row });
      setLoadState('ready');
    }

    fetchSite().then(applyData).catch(() => applyData(JSON.parse(JSON.stringify(DEFAULT_SITE))));
    return () => {
      cancelled = true;
    };
  }, [navigate, articleId]);

  async function onUpload(file) {
    if (!file) return;
    try {
      const { url } = await uploadFile(file);
      setArticle((a) => (a ? { ...a, image: url } : a));
    } catch (e) {
      setErr(e.message || 'Upload failed');
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!article || !articleId) return;
    setErr('');
    setSaving(true);
    try {
      await updateNewsArticle(decodeURIComponent(articleId), article);
      navigate('/admin/dashboard/news');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loadState === 'loading') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  if (loadState === 'notfound' || !article) {
    return (
      <>
        <header className="border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
          <Link to="/admin/dashboard/news" className="text-sm font-medium text-brand-olive hover:underline">
            ← Back to news
          </Link>
          <h2 className="mt-2 text-lg font-bold text-neutral-900">Article not found</h2>
        </header>
        <main className="px-4 py-8 md:px-8">
          <p className="text-neutral-600">No article matches this link.</p>
        </main>
      </>
    );
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <Link
            to="/admin/dashboard/news"
            className="text-xs font-medium text-brand-olive hover:underline"
          >
            ← Back to news
          </Link>
          <h2 className="mt-1 text-lg font-bold text-neutral-900 md:text-xl">Edit article</h2>
        </div>
      </header>

      <main className="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <form onSubmit={handleSave} className="w-full max-w-none rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <AdminImagePreview src={article.image} alt={article.alt} emptyLabel="Preview" />

          <ArticleForm article={article} onChange={setArticle} onUploadImage={onUpload} />

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-forest px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <Link
              to="/admin/dashboard/news"
              className="rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </>
  );
}
